import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";
import { AlertManager } from "client/Hooks";
import { openFileExplorer } from "client/Misc/domUtils";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useCallback
} from "react";
import { isNotNil } from "client/Misc/utils";
import { PixabayGalleryImage, usePixabayGalleryDialog } from "client/Features/ImageGallery/PixabayGalleryDialog";
import { ImageAttributionKind, ImageAttributionSaveModel } from "client/Api/common";
import { useSelectSingleImageFromDocumentPicker } from "../useSelectSingleImageFromDocumentPicker";
import { useCompressImageBeforeUpload } from "../CompressImageDialog/useCompressImageBeforeUpload";

export type HubImageSelectorKind =
	| "device"
	| "workspace"
	| "pixabay";

export type ImageWithAttribution = {
	file: File;
	attribution: ImageAttributionSaveModel | null;
};

export type HubImageSelectorResults = {
	dialogElement: ReactElement | null;
	onSelect: (kind: HubImageSelectorKind) => Promise<TryResult<ImageWithAttribution>>;
	onResolveFileForUpload: (selectedFile: File) => Promise<File>;
};

export type HubImageSelectorOptions = {
	alertManager: AlertManager;
	supportedImageExtensions: readonly string[];
	supportedMimeTypes: readonly string[];
	defaultImageLocation: DocumentLocationViewModel | null;
	maxImageSize: number;
	onIsBusyChange: (isBusy: boolean) => void;
};

export function useHubImageSelector(
	options: HubImageSelectorOptions
): HubImageSelectorResults {
	const {
		alertManager,
		defaultImageLocation,
		supportedImageExtensions,
		supportedMimeTypes,
		maxImageSize,
		onIsBusyChange
	} = options;

	const [pixabayDialog, openPixabayDialog] = usePixabayGalleryDialog();

	const [compressImageDialog, resolveFileForUpload] = useCompressImageBeforeUpload(
		maxImageSize,
		onIsBusyChange
	);

	const {
		dialogElement: selectImageDialog,
		onSelectImage: onSelectImageFromWorkspace
	} = useSelectSingleImageFromDocumentPicker({
		onIsBusyChange,
		supportedImageExtensions,
		defaultImageLocation,
		alertManager
	});

	const selectHandler = useCallback(
		async (kind: HubImageSelectorKind): Promise<TryResult<ImageWithAttribution>> => {
			switch (kind) {
				case "device":
					const selectFromDeviceResults: TryResult<readonly File[]> = await openFileExplorer(supportedMimeTypes);

					if (!selectFromDeviceResults.wasSuccessful) {
						return { wasSuccessful: false };
					}

					const deviceFile: File = await resolveFileForUpload(selectFromDeviceResults.result[0]);

					return {
						wasSuccessful: true,
						result: {
							file: deviceFile,
							attribution: null
						}
					};
				case "workspace":
					const selectFromWorkspaceResults: TryResult<File> = await onSelectImageFromWorkspace();

					if (!selectFromWorkspaceResults.wasSuccessful) {
						return { wasSuccessful: false };
					}

					const workspaceFile: File = await resolveFileForUpload(selectFromWorkspaceResults.result);

					return {
						wasSuccessful: true,
						result: {
							file: workspaceFile,
							attribution: null
						}
					};
				case "pixabay":
					const selectFromPixabayResults: TryResult<PixabayGalleryImage> = await openPixabayDialog({});

					if (!selectFromPixabayResults.wasSuccessful) {
						return { wasSuccessful: false };
					}

					const {
						file,
						metadata
					} = selectFromPixabayResults.result;

					const {
						author
					} = metadata;

					const pixabayFile: File = await resolveFileForUpload(file);

					return {
						wasSuccessful: true,
						result: {
							file: pixabayFile,
							attribution: author
								? {
									kind: ImageAttributionKind.Pixabay,
									pixabay: {
										authorId: author.id,
										authorName: author.name
									}
								}
								: null
						}
					};
				default:
					never(kind);
			}
		},
		[
			supportedMimeTypes,
			onSelectImageFromWorkspace,
			openPixabayDialog,
			resolveFileForUpload
		]
	);

	const dialogElement: ReactElement | nil = [
		selectImageDialog,
		pixabayDialog,
		compressImageDialog
	].find(isNotNil);

	return {
		dialogElement: dialogElement ?? null,
		onSelect: selectHandler,
		onResolveFileForUpload: resolveFileForUpload
	};
}